import { useState, useEffect } from "react";

type AppLoadProps = {
  isProgressStartAutomatically?: boolean;
  incrementProgressValue?: number;
  /**
   * setIntervalue value i.e. to update the progress value after each interval
   */
  progressInterval?: number;
  /**
   * after progressInterval stopped, how many seconds we should wait to complete the progress
   */
  successLoadedTimeout?: number;
  /**
   * At which progress value we want to stop the progress
   */
  stoppedProgressValue?: number;
  /**
   * if true then it will automatically complete the progress after 'successLoadedTimeout'
   * otherwise we have to manually call done()
   */
  isManualProgressCompleted?: boolean;
};

export default function useAppLoad(props?: AppLoadProps) {
  const {
    isProgressStartAutomatically = true,
    progressInterval = 500,
    incrementProgressValue = 10,
    successLoadedTimeout = 3000,
    stoppedProgressValue = 70,
    isManualProgressCompleted = true,
  } = props ?? {};

  const [progress, setProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isProgressComplete, setIsProgressComplete] = useState(false);

  const initiateProgress = () => {
    const intervalId = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= stoppedProgressValue) {
          window.clearInterval(intervalId);
          return stoppedProgressValue;
        }

        const nextValue = prev + incrementProgressValue;

        if (nextValue >= stoppedProgressValue) {
          window.clearInterval(intervalId);
          return stoppedProgressValue;
        }

        return nextValue;
      });
    }, progressInterval);

    return intervalId;
  };

  useEffect(() => {
    let intervalId: number | undefined;
    if (isProgressStartAutomatically) {
      intervalId = initiateProgress();
    }

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let timeoutId: number | undefined;
    if (progress >= stoppedProgressValue && isManualProgressCompleted) {
      timeoutId = window.setTimeout(() => {
        setProgress(100);
        setIsProgressComplete(true);
      }, successLoadedTimeout);
    } else if (progress >= stoppedProgressValue && !isManualProgressCompleted) {
      timeoutId = window.setTimeout(() => {
        setProgress(100);
        setIsProgressComplete(true);
        setIsLoaded(true);
      }, successLoadedTimeout);
    }

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [progress, stoppedProgressValue, isManualProgressCompleted, successLoadedTimeout]);

  const start = () => {
    initiateProgress();
  };

  const done = () => {
    if (isProgressComplete) {
      setIsLoaded(true);
    }
  };

  return { progress, isLoaded, isProgressComplete, start, done };
}
